import React, { useRef, useState } from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';
import { useColorScheme } from 'nativewind';
import { Button, Icon, RadioGroup, ResultCounter } from '../../components';
import svgIcons from '../../assets';
import { cln } from '../../utils/classnames';
import {
  HelpdeskCategories,
  Pagination as IPagination,
  CategoriesStatusFilter,
} from '../../client/interfaces';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { AppNavigationProp, ScreenName } from '../../navigation/types';

interface Props {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  selectedStatus: CategoriesStatusFilter | undefined | string;
  setSelectedStatus: React.Dispatch<React.SetStateAction<string | undefined>>;
  emptyFilters: () => void;
  activeTab: HelpdeskCategories;
  pagination: IPagination;
  navigation: AppNavigationProp;
}

const AdminCategoryFilter: React.FC<Props> = ({
  search,
  setSearch,
  selectedStatus,
  setSelectedStatus,
  emptyFilters,
  activeTab,
  pagination,
  navigation,
}) => {
  const searchInputRef = useRef(null);
  const [searchActive, setSearchActive] = useState(false);
  const { colorScheme } = useColorScheme();
  const { t } = useAppTranslation();
  // Placeholder colors
  const getPlaceholderColor = (isActive: boolean) => {
    if (!isActive) {
      return colorScheme === 'dark' ? '#888888' : '#101010';
    }
    return colorScheme === 'dark' ? '#101010' : '#888888';
  };
  return (
    <View className="mt-10 mb-6">
      <View className="flex-row items-center h-12 w-full">
        <View className="absolute ml-4 z-20">
          <Icon
            icon={svgIcons.searchIcon}
            mobileSize={{ width: 24, height: 24 }}
            webSize={{ width: 24, height: 24 }}
            inverted={searchActive}
          />
        </View>
        <TextInput
          ref={searchInputRef}
          placeholderTextColor={getPlaceholderColor(searchActive)}
          className={cln(
            searchActive
              ? 'text-neutral-50 bg-neutral-950 dark:text-neutral-950 dark:bg-neutral-50'
              : 'text-neutral-950 bg-neutral-50 dark:text-neutral-50 dark:bg-neutral-950',
            'h-12 border-[1px] flex-1 pl-[52px] pr-3 border-neutral-900 dark:border-neutral-200',
          )}
          focusable={false}
          onChangeText={setSearch}
          onFocus={() => {
            setSearchActive(true);
          }}
          onBlur={() => setSearchActive(false)}
          value={search}
          keyboardType="ascii-capable"
          placeholder={t('common:search')}
          onSubmitEditing={() => setSearchActive(false)}
        />
      </View>
      <View className="flex-row items-end mt-10">
        <View className="flex-row items-center mb-[6px]">
          <Text className="text-input text-neutral-950 dark:text-neutral-50 font-[sans-600] mr-[6px]">
            {t('admin:search:status')}:
          </Text>

          <RadioGroup
            radioButtons={Object.values(CategoriesStatusFilter).map((status) => ({
              id: status,
              label: t(`admin:search:${status}`),
              value: status,
            }))}
            selectedId={selectedStatus}
            onPress={setSelectedStatus}
            layout={'row'}
          />
        </View>

        <View className="flex-col ml-auto relative">
          <View className="relative h-8 flex-row items-center justify-end bottom-2 right-0">
            <ResultCounter pagination={pagination} classNames="mb-0" />
            {(!!search || selectedStatus !== CategoriesStatusFilter.all) && (
              <Pressable className="justify-center items-center ml-5" onPress={emptyFilters}>
                <Text className="text-primary-600 dark:text-primary-500 text-body font-[sans-400]">
                  {t('articles:remove_search')}
                </Text>
              </Pressable>
            )}
          </View>
          <Button
            text={
              activeTab === HelpdeskCategories.topics
                ? t('admin:helpdesk_categories:add_new_topic')
                : t('admin:helpdesk_categories:add_new_organization')
            }
            variant={'fill'}
            onPress={() => {
              navigation.navigate(
                activeTab === HelpdeskCategories.topics
                  ? ScreenName.AdminHelpdeskTopicEditScreen
                  : ScreenName.AdminHelpdeskOrganizationEditScreen,
              );
            }}
            size={'medium'}
            style={{ alignItems: 'flex-end' }}
          />
        </View>
      </View>
    </View>
  );
};

export default AdminCategoryFilter;
